import React from 'react'
import { XStack, YStack } from 'tamagui'
import { Trans } from '@lingui/react/macro'
import { useLingui } from '@lingui/react/macro'

import { Card } from '@newComponents/Card'
import { Icon } from '@newComponents/Icon'
import { Paragraph } from '@newComponents/Text'
import { Link } from '@newComponents/Link'

import { DocSection } from '../shared/DocSection'
import { DocScreenshot } from '../shared/DocScreenshot'

const Profile = () => {
  const { t } = useLingui()

  return (
    <>
      <Card gap='$4'>
        <Paragraph size='$5'>
          <Trans>
            Your Guild profile page is the front door of your community — the first
            thing visitors see when they navigate to your Guild's URL. It showcases
            who you are, what you do, and what's coming up next. Everything a
            visitor needs to understand and join your community is right here.
          </Trans>
        </Paragraph>
        <DocScreenshot
          src='docs/guilds/guild-profile-page.png'
          alt={t`Toronto JavaScript guild profile page showing cover photo, profile photo, description, 15 members, Join button, and tab navigation for Guild, Events, Presentations, and Members`}
          caption={t`The Guild profile page — your community at a glance`}
        />
      </Card>

      <Card gap='$4'>
        <DocSection id='profile-header' title={t`Profile Header`}>
          <Paragraph size='$5'>
            <Trans>The header area at the top of the page includes:</Trans>
          </Paragraph>
          <YStack gap='$3'>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='image' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Cover photo</Paragraph> — A wide
                  banner image that sets the visual tone for your Guild. Choose something
                  that represents your community, such as a photo from a past Event, your
                  city skyline, or branded artwork.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='image' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Profile photo</Paragraph> — A
                  smaller image that appears alongside your Guild's name in search
                  results, Event cards, and Member lists. Logos work well here.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='edit' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Guild name and description</Paragraph>{' '}
                  — Your community's name and a short description of what the Guild is
                  about. The description supports rich text so you can include links and
                  formatting.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='people' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Member count</Paragraph> — The
                  total number of Members in the Guild, giving visitors a sense of the
                  community's size.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='person-add' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Join button</Paragraph> — A
                  prominent button that lets visitors join your Guild instantly. If the
                  Guild has a{' '}
                  <Link href='/docs/guilds/forms'>Membership Form</Link>, clicking Join
                  opens the form instead.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='link' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Social links</Paragraph> —
                  If the Guild has social profiles configured in{' '}
                  <Link href='/docs/guilds/settings'>Guild Settings</Link> (Website,
                  Bluesky, Twitter/X, GitHub, LinkedIn, Instagram, or YouTube), they
                  appear as icon links in the header area. Members also display their
                  own social links on their Member cards throughout the Guild.
                </Trans>
              </Paragraph>
            </XStack>
          </YStack>
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='tab-navigation' title={t`Tab Navigation`}>
          <Paragraph size='$5'>
            <Trans>
              Below the header, visitors can navigate between four tabs that organize
              your Guild's content:
            </Trans>
          </Paragraph>
          <YStack gap='$3'>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='home' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Guild</Paragraph> — The main
                  overview tab and the default landing page. It shows your Guild's
                  description, upcoming Events, recent Presentations, and featured
                  Members — a snapshot of everything your community has to offer.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='calendar' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Events</Paragraph> — A complete
                  list of your Guild's upcoming and past{' '}
                  <Link href='/docs/events/overview'>Events</Link>. Upcoming Events are
                  shown first so visitors can quickly see what's next, with past Events
                  available below for reference.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='projection-screen' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Presentations</Paragraph> — A
                  library of recorded talks, slide decks, and session content from your{' '}
                  <Link href='/docs/presentations/overview'>Presentations</Link>. This
                  tab gives your community's knowledge a permanent, browsable home.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='people' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Members</Paragraph> — The people
                  who make up your community, grouped by role: Organizers, Editors,
                  Presenters, and Members. See{' '}
                  <Link href='/docs/guilds/members'>Managing Members</Link> for more on
                  roles and permissions.
                </Trans>
              </Paragraph>
            </XStack>
          </YStack>
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='customizing-profile' title={t`Customizing Your Profile`}>
          <Paragraph size='$5'>
            <Trans>
              Organizers can update the Guild's profile at any time through{' '}
              <Link href='/docs/guilds/settings'>Guild Settings</Link>. You can
              change your cover photo, profile photo, name, description, short name
              (URL slug), and timezone. Keeping your profile up to date helps attract
              new Members and gives returning visitors a reason to come back.
            </Trans>
          </Paragraph>
        </DocSection>
      </Card>
    </>
  )
}

export default Profile
